import React from "react";
import type Hls from "hls.js";

const MAX_FATAL_RECOVERY_ATTEMPTS = 2;

export type HlsVideoSource = {
  mimeType?: string | null;
  src?: string | null;
  type?: string | null;
};

export function isHlsVideoSource(source: HlsVideoSource | undefined): boolean {
  const mime = `${source?.mimeType ?? ""} ${source?.type ?? ""}`.toLowerCase();
  if (mime.includes("mpegurl") || mime.includes("video/hls")) return true;

  if (!source?.src) return false;
  try {
    return new URL(source.src, "https://ouster.invalid").pathname.toLowerCase().endsWith(".m3u8");
  } catch {
    return source.src.toLowerCase().split(/[?#]/)[0]?.endsWith(".m3u8") ?? false;
  }
}

type Props = {
  autoPlay?: boolean;
  enabled?: boolean;
  onError?: () => void;
  onReady?: () => void;
  source?: HlsVideoSource;
  videoRef: React.RefObject<HTMLVideoElement | null>;
};

export function useHlsVideo({ autoPlay = false, enabled = true, onError, onReady, source, videoRef }: Props): boolean {
  const isHls = isHlsVideoSource(source);

  React.useEffect(() => {
    const video = videoRef.current;
    const src = source?.src;
    if (!enabled || !isHls || !video || !src) return;

    let cancelled = false;
    let failed = false;
    let progressiveFallbackStarted = false;
    let hls: Hls | null = null;

    const reportError = () => {
      if (cancelled || failed) return;
      failed = true;
      onError?.();
      hls?.destroy();
      hls = null;
    };
    const startProgressiveFallback = () => {
      if (cancelled || failed || progressiveFallbackStarted) {
        if (!progressiveFallbackStarted) reportError();
        return;
      }

      progressiveFallbackStarted = true;
      hls?.destroy();
      hls = null;
      video.addEventListener("error", reportError);
      video.src = src;
      video.load();
    };
    const markReady = () => {
      if (cancelled || failed) return;
      onReady?.();
      if (autoPlay) void video.play().catch(() => undefined);
    };
    video.addEventListener("loadedmetadata", markReady);
    video.addEventListener("canplay", markReady);

    const nativeHls = Boolean(video.canPlayType("application/vnd.apple.mpegurl"));
    if (nativeHls) {
      video.addEventListener("error", reportError);
      video.src = src;
      video.load();
    } else {
      void import("hls.js")
        .then(({ default: Hls }) => {
          if (cancelled || !Hls.isSupported()) {
            if (!cancelled) startProgressiveFallback();
            return;
          }

          const instance = new Hls({
            capLevelToPlayerSize: true,
            maxBufferLength: 20,
          });
          let mediaRecoveryAttempts = 0;
          let networkRecoveryAttempts = 0;
          hls = instance;
          instance.loadSource(src);
          instance.attachMedia(video);
          instance.on(Hls.Events.MANIFEST_PARSED, markReady);
          instance.on(Hls.Events.ERROR, (_event, data) => {
            if (!data.fatal || cancelled || failed) return;
            if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
              if (networkRecoveryAttempts < MAX_FATAL_RECOVERY_ATTEMPTS) {
                networkRecoveryAttempts += 1;
                instance.startLoad();
                return;
              }
              startProgressiveFallback();
              return;
            }
            if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
              if (mediaRecoveryAttempts < MAX_FATAL_RECOVERY_ATTEMPTS) {
                mediaRecoveryAttempts += 1;
                instance.recoverMediaError();
                return;
              }
              startProgressiveFallback();
              return;
            }
            startProgressiveFallback();
          });
        })
        .catch(() => {
          if (!cancelled) startProgressiveFallback();
        });
    }

    return () => {
      cancelled = true;
      video.removeEventListener("loadedmetadata", markReady);
      video.removeEventListener("canplay", markReady);
      video.removeEventListener("error", reportError);
      hls?.destroy();
      if (video.getAttribute("src") === src) {
        video.removeAttribute("src");
        video.load();
      }
    };
  }, [autoPlay, enabled, isHls, onError, onReady, source?.mimeType, source?.src, source?.type, videoRef]);

  return isHls;
}
